import type { SupplyPackage } from './api';
import { leerCantidad } from './numeros';

/**
 * Los empaques en que llega un insumo, como se teclean en su ficha, y la
 * cuenta del conteo por empaque.
 *
 * Reglas puras, para probarlas sin pantalla. Ver docs/15 §2.52.
 */

export interface EmpaqueTecleado {
  id?: string;
  nombre: string;
  trae: string;
  codigo: string;
}

/** Lo que le falta a la lista para poder guardarse, o nulo si está lista. */
export function problemaDeEmpaques(lista: readonly EmpaqueTecleado[]): string | null {
  const nombres = new Set<string>();
  for (const empaque of lista) {
    const nombre = empaque.nombre.trim();
    if (nombre === '') return 'Cada empaque lleva su nombre: «Rollo 100 m».';
    if (nombres.has(nombre.toLowerCase())) return `Dos empaques se llaman «${nombre}».`;
    nombres.add(nombre.toLowerCase());
    const trae = leerCantidad(empaque.trae);
    if (Number.isNaN(trae) || trae <= 0) return `Falta cuánto trae «${nombre}».`;
  }
  return null;
}

/** Cómo viaja al servidor: en la unidad del insumo, con tres decimales. */
export function paraEnviar(lista: readonly EmpaqueTecleado[]) {
  return lista.map((empaque) => ({
    ...(empaque.id === undefined ? {} : { id: empaque.id }),
    name: empaque.nombre.trim(),
    units: leerCantidad(empaque.trae).toFixed(3),
    barcode: empaque.codigo.trim() === '' ? null : empaque.codigo.trim(),
  }));
}

/**
 * Lo contado por empaque, sumado en la unidad del insumo: «3 rollos de 100 m
 * cerrados + 47 m del abierto» son 347 m.
 *
 * Abrir un rollo no es un movimiento (§2.52): el stock está en metros, y lo
 * cerrado o abierto solo ayuda a contar sin desenrollar. Nulo si algo de lo
 * tecleado no es una cantidad.
 */
export function totalPorEmpaques(
  empaques: readonly SupplyPackage[],
  cerrados: Readonly<Record<string, string>>,
  sueltos: string,
): number | null {
  let total = sueltos.trim() === '' ? 0 : leerCantidad(sueltos);
  if (Number.isNaN(total)) return null;
  for (const empaque of empaques) {
    const texto = (cerrados[empaque.id] ?? '').trim();
    if (texto === '') continue;
    const cuantos = leerCantidad(texto);
    if (Number.isNaN(cuantos) || !Number.isInteger(cuantos)) return null;
    total += cuantos * Number.parseFloat(empaque.units);
  }
  // Tres decimales, como todo lo que viaja: el metro y el gramo.
  return Math.round(total * 1000) / 1000;
}
